/**
 * Quiz-shaped activities whose only difference is the "question" area:
 *  - math: "a + b = ?" with the unknown shown as "?" until solved
 *  - missingLetter: the word with the hidden letter as "_"
 *  - missingWord: the phrase with the hidden word as "__________"
 */
import { useState } from "react";
import type { SlideOf } from "@makeit/schema";
import { QuestionView, QuizSlide } from "./QuizSlide";

const OPS: Record<string, string> = { sum: "+", subtraction: "-", multiplication: "x", division: "÷" };

export function MathSlide({ slide }: { slide: SlideOf<"math"> }) {
  const [solved, setSolved] = useState(false);
  const op = OPS[slide.operation] ?? "?";
  const show = (v: number, key: string) => (solved || slide.toGuess !== key ? fmt(v) : "?");
  const parts = [show(slide.element1, "element1"), solved || slide.toGuess !== "operation" ? op : "?", show(slide.element2, "element2"), "=", show(slide.result, "result")];
  if (slide.rtl) parts.reverse();
  const header = (
    <QuestionView element={null}>
      <div className="math-expression">{parts.join(" ")}</div>
    </QuestionView>
  );
  return <QuizSlide slide={slide} header={header} onEnd={() => setSolved(true)} />;
}

function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}

export function MissingLetterSlide({ slide }: { slide: SlideOf<"missingLetter"> }) {
  const [solved, setSolved] = useState(false);
  const word = slide.word?.text.content ?? "";
  const chars = Array.from(word);
  const shown = solved ? word : chars.map((c, i) => (i === slide.missingLetter ? "_" : c)).join("");
  // The word's image is shown like a quiz image (PlayspaceMissingLetter.cs)
  const withImage = slide.image ?? (slide.word?.image ? { ...slide.word, id: slide.word.id } : null);
  const header = (
    <QuestionView element={null}>
      <div className="word-masked">{shown}</div>
    </QuestionView>
  );
  return <QuizSlide slide={{ ...slide, image: withImage }} header={header} onEnd={() => setSolved(true)} />;
}

export function MissingWordSlide({ slide }: { slide: SlideOf<"missingWord"> }) {
  const [solved, setSolved] = useState(false);
  const words = (slide.phrase?.text.content ?? "").split(/[\n ]+/).filter(Boolean);
  const shown = words.map((w, i) => (i === slide.missingWord && !solved ? "__________" : w)).join(" ");
  const header = (
    <QuestionView element={slide.phrase ? { ...slide.phrase, text: { ...slide.phrase.text, content: "" } } : null}>
      <div className="phrase-masked" style={slide.phrase ? { color: slide.phrase.text.color } : undefined}>
        {shown}
      </div>
    </QuestionView>
  );
  return <QuizSlide slide={slide} header={header} onEnd={() => setSolved(true)} />;
}
